import { Component, OnInit, Directive, SimpleChanges, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
import { MessageService } from '../../../services/message.service'
import { MasterService } from '../../../services/master.service'
import { StoreService } from '../../../services/store.service'
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import * as languageLibrary from '../../../services/language'

// external
import moment from 'moment';
import { SEARCH_DEBOUNCE_MILLIS, MIN_SEARCH_CHARS, SCROLL_BOTTOM_OUT } from "src/app/constants/list";
import { SecurityService } from 'src/app/services/security.service';
import { ListHeader } from 'src/app/models/list-header.model';
import { Inventory } from 'src/app/models/inventory.model';
import { InventoryStatus } from 'src/app/models/inventory-status.model';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-inventory',
    templateUrl: './inventory.component.html',
    styleUrls: ['./inventory.component.scss'],
    standalone: false
})
export class InventoryComponent implements OnInit, OnDestroy {

    /*
    * Variables
    */

    private readonly FILTER_INQUIRY_KEY = 'flt-inv-q'
    private readonly FILTER_STATUS_KEY = 'flt-inv-s'
    protected readonly FILTER_SPAN_KEY = 'flt-inv-s'

    //
    private images = []

    // save the language
    protected language: any = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    // set all words
    protected words = languageLibrary.language

    // save all data by type
    protected vehicles: Inventory[] = []

    // inventorySetup
    protected inventorySetup: any;

    // save all data by type (complete)
    private dataComplete: Object[] = []

    // define lot selected
    protected lotSelected: any = {}

    // the selected inventory
    protected selectedInventory: Inventory | null = null;

    // table headers
    protected headers: ListHeader[] = [
        { name: 'Vehicle Image', param: null, view: true, disabled: true },
        { name: 'Stock #', param: null, view: true, disabled: true },
        { name: 'VIN #', param: null, view: true, disabled: true },
        { name: 'Year', param: null, view: true, disabled: true },
        { name: 'Make', param: null, view: true, disabled: true },
        { name: 'Model', param: null, view: true, disabled: true },
        { name: 'Trim Level', param: null, view: true, disabled: false },
        { name: 'Body', param: null, view: true, disabled: false },
        { name: 'Engine', param: null, view: true, disabled: false },
        { name: 'Cylinders', param: null, view: true, disabled: false },
        { name: 'Transmission', param: null, view: true, disabled: false },
        { name: 'Driveline', param: null, view: true, disabled: false },
        { name: 'Exterior Color', param: null, view: true, disabled: true },
        { name: 'Interior Color', param: null, view: true, disabled: false },
        { name: 'Exterior Color (Minor)', param: null, view: true, disabled: false },
        { name: 'Selling Price', param: null, view: true, disabled: false },
        { name: 'Type', param: null, view: true, disabled: false },
        { name: 'Miles', param: null, view: true, disabled: true },
        { name: 'Odometer', param: null, view: true, disabled: false },
        { name: 'Curb Weight', param: null, view: true, disabled: false },
        { name: 'CarryingWeight', param: null, view: true, disabled: false },
        { name: 'GVWR', param: null, view: true, disabled: false },
        { name: 'Tonnage', param: null, view: true, disabled: false },
        { name: 'Lot', param: null, view: true, disabled: true },
        { name: 'Status', param: null, view: true, disabled: true },
        { name: 'Days Old', param: null, view: true, disabled: true }
    ]

    // define if is loading
    protected loading: boolean = false

    // define if something is uploading
    private uploading: boolean = false

    // permissions

    // Current page the user is viewing (last loaded).
    private page = 1

    // The number contacts currently being displayed.
    protected displayed = 0

    // Indicates whether or not there are no more records to load.
    private exhausted = false

    // Total number of records available
    protected records: number = 0

    // The search form group
    protected form: FormGroup | null = null

    // The current search text to filter the list by.
    private inquiry: string | null = null

    // A debounce timer for search events. Used to wait for 
    // the user to stop typing before we fetch.
    private watcher: ReturnType<typeof setTimeout> | null = null

    // Keep track of how the user wants to sort list.
    private sortables: Object = { 'received': 'DESC' }

    // list of status
    protected statuses: InventoryStatus[] = []

    //
    private inventorySetupNotFound = false

    //
    protected isAdmin = false

    // 
    private scannedVINImages = []

    //
    private userId: Number | null = null;

    private subscriptions: Subscription[] = []

    /*
    todo: Life Cycles
    */
    constructor(private route: ActivatedRoute, private router: Router, private ms: MessageService, private master: MasterService, private store: StoreService, protected security: SecurityService, private fb: FormBuilder) { }

    ngOnInit() {
        this.lotSelected = this.store.lotSelected ? this.store.lotSelected : localStorage.getItem('lot')

        this.getInventoryStatuses()

        this.form = this.fb.group({
            inquiry: new FormControl<string>('', []),
            status: new FormControl<number>(0, []),
            span: new FormControl<string>('', []),
            from: new FormControl<string>('', []),
            to: new FormControl<string>('', [])
        })

        this.readFilters()

        this.subscriptions.push(
            this.form.get('inquiry').valueChanges.subscribe(inquiry => {
                if (this.watcher) {
                    clearTimeout(this.watcher)
                    this.watcher = null
                }

                if (this.inquiry === inquiry) {
                    return
                }

                this.watcher = setTimeout(() => {
                    this.inquiry = inquiry
                    this.writeFilters()
                    this.reset()
                    this.fetch()
                }, SEARCH_DEBOUNCE_MILLIS)
            })
        )

        this.subscriptions.push(
            this.form.get('status').valueChanges.subscribe(status => {
                this.writeFilters()
                this.reset()
                this.fetch()
            })
        )

        this.subscriptions.push(
            this.form.get('span').valueChanges.subscribe(span => {
                this.writeFilters()
                this.reset()
                this.fetch()
            })
        )

        if (!this.store.userAccount['capabilities']) {
            return
        }

        this.getInventorySetup()
    }
    
    ngOnDestroy(): void {
        for (const subscription of this.subscriptions) {
            subscription.unsubscribe()
        }

        this.subscriptions = []

        if (this.watcher !== null) {
            clearTimeout(this.watcher)
            this.watcher = null
        }
    }

    ngOnChanges(changes: SimpleChanges): void {

    }

    /**
    * Reset the pagination parameters and scroll to the top
    */
    private reset() {
        this.page = 1
        this.exhausted = false
        this.displayed = 0

        const list = document.querySelector('#listing')
        if (!list) {
            return
        }

        list.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto'
        })
    }

    /*
    * Functions
    */

    public onClick(event: any): void {
        event.stopPropagation();
    }

    public getInventoryStatuses() {
        this.master.get(`collections/inventory/statuses`, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.statuses = res.data.inventoryStatuses
        })
    }

    /*
    todo: pagination and rows per view functions
    */
    public toggleCell(index?: number) {
        if (index != undefined) {
            this.headers[index]['view'] = !this.headers[index]['view']
        }
    }

    // save View
    public saveView = (): void => {
        let config = JSON.stringify(this.headers)
        this.master.put(`user/${this.userId}`, { users: { inventoryTable: config, id: this.userId } }, res => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }
            document.getElementById('btn-customizeColumns-close').click()
        })
    }

    // generate a inventory object
    public buildData = (data: any[]): any[] => {
        let newData: any[] = []

        if (!data || data.length <= 0) {
            return newData
        }

        for (let index = 0; index < data.length; index++) {
            newData.push({
                'id': data[index]['id'],
                'Vehicle Image': (data[index].uploads && data[index].uploads.length > 0) ? data[index].uploads[0]['url'] : '',
                'Stock #': data[index].stockNumber,
                'VIN #': data[index].vehicle.vinNumber,
                'Year': data[index].vehicle.modelYear,
                'Make': data[index].vehicle.make,
                'Model': data[index].vehicle.model,
                'Trim Level': data[index].vehicle.trimLevel,
                'Body': data[index].vehicle.bodyStyle,
                'Engine': data[index].vehicle.engine,
                'Cylinders': data[index].vehicle.cylinders,
                'Transmission': data[index].vehicle.transmission,
                'Driveline': data[index].vehicle.driveline,
                'Exterior Color': data[index].exteriorColor,
                'Interior Color': data[index].interiorColor,
                'Exterior Color (Minor)': data[index].exteriorColorMinor,
                'Selling Price': data[index].sellingPrice,
                'Type': data[index].vehicle.type,
                'Miles': data[index].miles,
                'Odometer': data[index].odometer,
                'Curb Weight': data[index].vehicle.curbWeight,
                'GVWR': data[index].vehicle.gvwr,
                'Tonnage': data[index].vehicle.tonnage,
                'Lot': data[index].lot.contact.name,
                'Status': data[index].inventoryStatus.name,
                'StatusId': data[index].statusId,
                'Days Old': this.calculateDaysOld(data[index].dateOnLot),
                stockNumber: data[index].stockNumber,
                selectable: data[index].inventoryStatus.selectable,
            })
        }
        return newData
    }

    public calculateDaysOld = (dateOnLotVar) => {
        if (dateOnLotVar === null) {
            return 0
        }

        let dateOnLot = moment(dateOnLotVar);
        let now = moment(moment().format('YYYY-MM-DD'))
        return now.diff(dateOnLot, 'days') + 1
    }

    // change status
    public modifyStatus = (statusId: number, inventoryId: number): void => {
        this.master.post(`inventory/${inventoryId}/status`, { information: { statusId: statusId, inventoryId: inventoryId, startDate: new Date().toISOString().substring(0, 10) } }, res => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.ms.sendMessage("alert", { type: "success", text: this.words[this.language].saved })
        })
    }

    // move the position of columns in the table
    public drop(event: CdkDragDrop<string[]>) {
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex)
            return
        }

        transferArrayItem(event.previousContainer.data,
            event.container.data,
            event.previousIndex,
            event.currentIndex)
    }

    // todo: listen to output of navbar
    public listenerNav = (e): void => {
        switch (e['message']) {
            case 'changeLanguage':
                this.language = e['value']
                break;
            case 'changeLot':
                this.vehicles = []
                if (this.lotSelected != e.lot) {
                    this.lotSelected = e.lot
                    this.getInventorySetup()
                }
                break
            case 'setPermissions':
                this.reset()
                this.fetch()
                break
            default:
                break
        }
    }

    // edit inventory
    public editInventory = (id: number, stockNumber: any): void => {
        this.loading = true
        this.router.navigate([`/inventory/${id}`])
        return
    }

    public createInventory = (): void => {
        if (this.inventorySetupNotFound) {
            return
        }

        this.router.navigate([`/inventory/add`])
    }

    /**
    * Handle scroll events of the list container and load 
    * the next page when avilable and the user scrolled 
    * to the bottom.
    * @param e The scroll event that trigggered this handler.
    */
    public scrolled = (e): void => {
        if (!e || !e.target) {
            return
        }

        if (this.exhausted) {
            return
        }

        if (this.loading) {
            return
        }

        const location = Math.abs(e.target.scrollHeight - e.target.clientHeight - e.target.scrollTop)

        if (location >= SCROLL_BOTTOM_OUT) {
            return
        }

        this.page = this.page + 1
        this.fetch()
    }

    /**
    * 
    * @param field The field that user wants to sort by. 
    */
    public sort = (field: string): void => {
        if (!field || (field.length <= 0)) {
            return
        }

        if (this.sortables[field]) { // need to toggle the direction?
            switch (this.sortables[field]) {
                case 'ASC':
                    this.sortables[field] = 'DESC'
                    break
                default:
                    this.sortables[field] = 'ASC'
                    break
            }

            this.reset()
            this.fetch()
            return
        }

        this.sortables = {}
        this.sortables[field] = 'ASC'
        this.reset()
        this.fetch()
    }

    public sortedAsc = (field: string): Boolean => {
        if (!field || (field.length <= 0) || !this.sortables) {
            return false
        }

        if (!this.sortables[field]) {
            return false
        }

        return (this.sortables[field].toUpperCase() === 'ASC')
    }

    public sortedDesc = (field: string): Boolean => {
        if (!field || (field.length <= 0) || !this.sortables) {
            return false
        }

        if (!this.sortables[field]) {
            return false
        }

        return (this.sortables[field].toUpperCase() === 'DESC')
    }

    /**
    * Acquire contact list by given type
    */
    public fetch = (): void => {
        if (this.security.notPermitted('inventory', 'inventory list', 'view')) {
            return
        }

        if (this.loading) { // already getting next page
            return
        }

        if (this.exhausted) { // currently showing all available records?
            return
        }

        this.loading = true

        let lotParam = ''
        if (this.lotSelected && (this.lotSelected > 0)) {
            lotParam = `&lotId=${parseInt(this.lotSelected)}`
        }

        let inquiryParam = ''
        if (this.inquiry && (this.inquiry.length > 0)) {
            inquiryParam = `&q=${encodeURIComponent(this.inquiry)}`
        }

        let statusParam = ''
        const status = parseInt(this.form.get('status').value)
        if (status && (!isNaN(status)) && (status > 0)) {
            statusParam = `&statuses=${status}`
        }

        let spanParam = ''
        const spanKey = this.form.get('span').value
        if (spanKey && (spanKey.length > 0)) {
            switch (spanKey) {
                case 'custom':
                    const from = this.form.get('from').value
                    const to = this.form.get('to').value
                    spanParam = `&from=${from}&to=${to}`
                    break
                default:
                    spanParam = `&span=${spanKey}`
                    break
            }
        }

        let sortParam = ''
        if (this.sortables && (Object.keys(this.sortables).length > 0)) {
            const sorts = []
            for (const key of Object.keys(this.sortables)) {
                let direction = ''
                if (this.sortables[key] && this.sortables[key].length > 0) {
                    direction = `|${this.sortables[key]}`
                }

                sorts.push(`${key}${direction}`)
            }

            sortParam = `&sort=${sorts.join(',')}`
        }

        this.master.get(`inventory?page=${this.page}${inquiryParam}${sortParam}${lotParam}${statusParam}${spanParam}`, (res) => {
            this.loading = false

            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.records = res.data.records
            this.displayed = Math.min(this.records, (res.data.page * res.data.ipp))
            this.exhausted = (res.data.next === null)

            if (this.page > 1) {
                this.vehicles = this.vehicles.concat(res.data.inventory)
                return
            }

            this.vehicles = res.data.inventory
        })
    }

    public getInventorySetup() {
        this.inventorySetupNotFound = false
        this.master.get(`lots/${this.lotSelected}/inventorySetup`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.isAdmin = this.security.permitted('corporation setup', 'lot detail', 'update')
            if (!this.isAdmin) {
                document.getElementById('btn-alertModalSetup').click();
                this.inventorySetupNotFound = true
                return
            }

            if (!res.data.inventorySetup || (!res.data.inventorySetup.defaultFormat)) {
                document.getElementById('btn-showInventorySetup').click();
                this.inventorySetupNotFound = true
                return
            }

            this.inventorySetup = res.data.inventorySetup
            this.reset()
            this.fetch()
        })
    }


    // todo: listener to childrens
    public listenerChildrens = (e): void => {
        if (e.message === 'closeModal') {
            (document.getElementById('btn-showInventorySetup-close') as HTMLButtonElement).click();
            this.inventorySetupNotFound = false
        }

        if (e.message === 'refreshInventoryList') {
            this.reset()
            this.fetch()
        }
    }

    public selectInventory(inventory: Inventory) {
        this.selectedInventory = inventory
    }


    public deleteInventory() {
        const btn = (document.getElementById('btn-close-modal-delete') as HTMLButtonElement)
        if (btn) { btn.click() }

        if (this.selectedInventory.status === 'Sold') {
            this.ms.sendMessage("alert", { type: "danger", text: 'Unable to discard a sold inventory item.' });
            return
        }

        this.master.discard(`inventory/${this.selectedInventory.id}`, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.reset()
            this.fetch()
        })
    }

    /**
    * Check session storage for stored filters and apply them if found.
    */
    private readFilters = () => {
        if (!sessionStorage) {
            return
        }

        const qFilter = sessionStorage.getItem(this.FILTER_INQUIRY_KEY)
        if (qFilter && (qFilter.length > 0)) {
            this.form.get('inquiry').setValue(qFilter, { emitEvent: false })
            this.inquiry = qFilter
        }

        const statusFilter = parseInt(sessionStorage.getItem(this.FILTER_STATUS_KEY))
        if (statusFilter && (!isNaN(statusFilter)) && (statusFilter > 0)) {
            this.form.get('status').setValue(statusFilter, { emitEvent: false })
        }

        const spanFilter = sessionStorage.getItem(this.FILTER_SPAN_KEY)
        if (spanFilter && (spanFilter.length > 0)) {
            if (spanFilter.startsWith('custom')) {
                const items = spanFilter.split(';')
                if (!items || (items.length !== 3)) {
                    return
                }

                this.form.get('span').setValue(items[0], { emitEvent: false })
                this.form.get('from').setValue(items[1], { emitEvent: false })
                this.form.get('to').setValue(items[2], { emitEvent: false })

                return
            }

            this.form.get('span').setValue(spanFilter, { emitEvent: false })
        }
    }

    /**
    * Save the existing list fitlers to session storage for later retrieval.
    */
    private writeFilters = () => {
        if (!sessionStorage) {
            return
        }

        const qFilter = this.form.get('inquiry').value
        if (!qFilter || (qFilter.length <= 0)) {
            sessionStorage.removeItem(this.FILTER_INQUIRY_KEY)
        }
        else {
            sessionStorage.setItem(this.FILTER_INQUIRY_KEY, qFilter)
        }

        const statusFilter = parseInt(this.form.get('status').value)
        if (!statusFilter || (isNaN(statusFilter)) || (statusFilter <= 0)) {
            sessionStorage.removeItem(this.FILTER_STATUS_KEY)
        }
        else {
            sessionStorage.setItem(this.FILTER_STATUS_KEY, statusFilter.toString())
        }

        const spanFilter = this.form.get('span').value
        if (!spanFilter || (spanFilter.length <= 0)) {
            sessionStorage.removeItem(this.FILTER_SPAN_KEY)
        }
        else {
            let fromTo = ''
            if (spanFilter === 'custom') {
                const from = this.form.get('from').value
                const to = this.form.get('to').value
                fromTo = `;${from};${to}`
            }

            sessionStorage.setItem(this.FILTER_SPAN_KEY, `${spanFilter}${fromTo}`)
        }
    }

    /**
    * Handle date span choices by the user.
    * @param choice 
    */
    public spanned = (choice: Object) => {
        if (!choice || (choice['key'] === undefined) || (choice['key'] === null)) {
            return
        }

        if (choice['key'] === 'custom') {
            const from = choice['from']
            const to = choice['to']
            if (!from || !to) {
                return
            }

            this.form.get('from').setValue(from, { emitEvent: false })
            this.form.get('to').setValue(to, { emitEvent: false })
        }

        this.form.get('span').setValue(choice['key'])
    }

    /**
     * Check to see if the current status of an inventory item is discardable.
     * @param status The current status of the inventory item.
     * @returns Indicates whether or not the inventory item can be discarded
     */
    protected discardable = (status: string): boolean => {
        if (!status || status.length <= 0) {
            return true
        }

        return (status.toLocaleLowerCase() !== 'sold')
    }

}
